/**
 * PulsingOrb Component
 *
 * CSS + Canvas hybrid pulsing orb for learner conversation UI.
 * No external dependencies - pure CSS animations with canvas glow effects.
 *
 * Props:
 * - isActive: whether the orb is responding (session active)
 * - audioLevel: 0-1 normalized audio level from voice session
 * - phase: current session phase (warm_up | in_character)
 *
 * Behavior:
 * - Idle: slow breathing animation (scale 1.0-1.05, 4s cycle)
 * - Active: responds to audioLevel (scale 1.0-1.3)
 * - Phase colors: warm_up = soft blue, in_character = brand sky
 * - Glow via box-shadow + filter: blur()
 */

import { useRef, useEffect, useMemo } from 'react';
import type { SessionPhase } from '../../lib/gemini/provider';

interface PulsingOrbProps {
  isActive: boolean;
  audioLevel: number; // 0-1
  phase: SessionPhase | 'idle';
}

/** Color configs per phase */
const PHASE_COLORS: Record<string, { primary: string; glow: string; bg: string }> = {
  idle: {
    primary: 'rgba(148, 163, 184, 0.6)', // slate-400
    glow: 'rgba(148, 163, 184, 0.2)',
    bg: 'rgba(148, 163, 184, 0.05)',
  },
  warm_up: {
    primary: 'rgba(96, 165, 250, 0.8)', // blue-400
    glow: 'rgba(96, 165, 250, 0.3)',
    bg: 'rgba(96, 165, 250, 0.08)',
  },
  transition: {
    primary: 'rgba(14, 165, 233, 0.8)', // sky-500
    glow: 'rgba(14, 165, 233, 0.3)',
    bg: 'rgba(14, 165, 233, 0.08)',
  },
  in_character: {
    primary: 'rgba(14, 165, 233, 0.9)', // sky-500 brand
    glow: 'rgba(14, 165, 233, 0.4)',
    bg: 'rgba(14, 165, 233, 0.1)',
  },
};

export function PulsingOrb({ isActive, audioLevel, phase }: PulsingOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const audioLevelRef = useRef(0);
  const smoothedLevelRef = useRef(0);

  // Update audio level ref for animation loop
  audioLevelRef.current = audioLevel;

  const colors = useMemo(() => PHASE_COLORS[phase] ?? PHASE_COLORS['idle']!, [phase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const draw = (time: number) => {
      animationRef.current = requestAnimationFrame(draw);

      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      const cx = w / 2;
      const cy = h / 2;

      ctx.clearRect(0, 0, w, h);

      // Smooth audio level (lerp toward target)
      const target = audioLevelRef.current;
      smoothedLevelRef.current += (target - smoothedLevelRef.current) * 0.15;
      const level = smoothedLevelRef.current;

      // Base radius: 30% of min dimension
      const baseRadius = Math.min(w, h) * 0.3;

      // Breathing animation (always active)
      const breathCycle = Math.sin(time / 2000) * 0.5 + 0.5; // 0-1 over 4s
      const breathScale = 1.0 + breathCycle * 0.05; // 1.0-1.05

      // Audio response (only when active)
      const audioScale = isActive ? 1.0 + level * 0.3 : 1.0; // 1.0-1.3

      const radius = baseRadius * breathScale * audioScale;

      // Outer glow rings (3 layers)
      const glowLayers = [
        { scale: 1.8, opacity: 0.05 + level * 0.05 },
        { scale: 1.4, opacity: 0.1 + level * 0.08 },
        { scale: 1.15, opacity: 0.15 + level * 0.1 },
      ];

      for (const layer of glowLayers) {
        const r = radius * layer.scale;
        const gradient = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, r);
        gradient.addColorStop(0, colors.glow);
        gradient.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.globalAlpha = layer.opacity;
        ctx.fill();
      }

      ctx.globalAlpha = 1;

      // Main orb gradient
      const mainGradient = ctx.createRadialGradient(
        cx - radius * 0.2, cy - radius * 0.2, 0,
        cx, cy, radius
      );
      mainGradient.addColorStop(0, colors.primary.replace(/[\d.]+\)$/, '1)'));
      mainGradient.addColorStop(0.7, colors.primary);
      mainGradient.addColorStop(1, colors.glow);

      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = mainGradient;
      ctx.fill();

      // Inner highlight (top-left)
      const highlightGradient = ctx.createRadialGradient(
        cx - radius * 0.3, cy - radius * 0.3, 0,
        cx - radius * 0.1, cy - radius * 0.1, radius * 0.6
      );
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      highlightGradient.addColorStop(1, 'transparent');

      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = highlightGradient;
      ctx.fill();
    };

    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [isActive, colors]);

  return (
    <div
      className="pulsing-orb-container"
      style={{ '--orb-glow': colors.glow, '--orb-bg': colors.bg } as React.CSSProperties}
    >
      <canvas ref={canvasRef} className="pulsing-orb-canvas" />
    </div>
  );
}
